import { motion } from 'framer-motion'
import { light } from '@fortawesome/fontawesome-svg-core/import.macro'
import { formatISO, startOfMinute } from 'date-fns'
import toast from 'react-hot-toast'
import { Button, Text } from 'src/components/ui'
import { FetchModel, GqlSetModelTrainingConfigurationInput } from 'src/services'
import { isDefined, isTimeRange, TimeRange } from 'src/types'
import { useUpdateModelTrainingConfiguration } from 'models/api'
import { useTrainingPeriodStore } from 'models/model'
import { ExcludedPeriod } from './ExcludedePeriod'

type Props = {
  model: FetchModel
}

export function ExcludedPeriods({ model }: Props): JSX.Element {
  const updateTrainingConfigMutation = useUpdateModelTrainingConfiguration()

  const {
    isModified,
    isEdit,
    trainingPeriod,
    excludedPeriods,
    resetState,
    editExcludedPeriod,
    removeExcludedPeriod,
    addExcludedPeriod,
    setIsEdit,
  } = useTrainingPeriodStore()

  async function handleApplyChanges(): Promise<void> {
    const uniquePeriods = excludedPeriods
      .filter(p => p.timeRange)
      .filter((period, idx, arr) => {
        if (!period.timeRange) return false
        return (
          arr.findIndex(
            p =>
              p.timeRange &&
              p.timeRange.from === period.timeRange?.from &&
              p.timeRange.to === period.timeRange.to,
          ) === idx
        )
      })

    const trainingConfig: GqlSetModelTrainingConfigurationInput = {
      modelId: model.id,
      exclusions: uniquePeriods
        .map(({ timeRange }) => timeRange)
        .filter(isDefined)
        .map(timeRange => ({
          start: formatISO(timeRange.from),
          end: formatISO(timeRange.to),
        })),
      trainingPeriod: isTimeRange(trainingPeriod)
        ? {
            timeRange: {
              start: formatISO(trainingPeriod.from),
              end: formatISO(trainingPeriod.to),
            },
          }
        : {
            period: trainingPeriod.period,
          },
    }
    await updateTrainingConfigMutation.mutateAsync(trainingConfig, {
      onError: () => {
        toast.error('Failed to update model training configuration', {
          position: 'top-right',
        })
      },
      onSuccess: () => {
        toast.success('Model training configuration updated', {
          position: 'top-right',
        })
      },
    })
  }

  async function handleResetState(): Promise<void> {
    resetState({
      id: model.id,
      trainingEnd: model.trainingEnd,
      trainingStart: model.trainingStart,
      trainingPeriod: model.trainingPeriod ?? undefined,
      excludedPeriods: model.trainingPeriodExclusions,
    })
    setIsEdit(false)
  }

  function handleTimeRangeChange(
    periodId: string,
    timeRangeInput: TimeRange,
  ): void {
    const timeRange = {
      from: startOfMinute(timeRangeInput.from).valueOf(),
      to: startOfMinute(timeRangeInput.to).valueOf(),
    }
    editExcludedPeriod(periodId, {
      timeRange,
    })
  }

  return (
    <motion.div
      onClick={e => e.stopPropagation()}
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.1 }}
      className="flex flex-col gap-xs rounded-2xs bg-[#F8F8F8] p-s"
    >
      <Text variant="description" className="opacity-70">
        The AI model will focus less on these periods
      </Text>
      <div className="flex flex-col items-start gap-s">
        {excludedPeriods.map(period => (
          <ExcludedPeriod
            key={period.id}
            period={period}
            isEdit={isEdit}
            onTimeRangeChange={timeRange =>
              handleTimeRangeChange(period.id, timeRange)
            }
            onHiddenChange={hidden =>
              editExcludedPeriod(period.id, {
                hidden,
              })
            }
            remove={() => removeExcludedPeriod(period.id)}
          />
        ))}
        <div className="flex w-full items-center justify-between">
          {isEdit ? (
            <>
              <Button
                disabled={excludedPeriods.some(p => !p.timeRange)}
                variant="icon-secondary"
                icon={light('plus')}
                onClick={() => addExcludedPeriod()}
                title="New Period"
              />
              <div className="flex items-center gap-xs">
                <Button
                  variant="secondary"
                  title={isModified ? 'Discard Changes' : 'Close'}
                  onClick={handleResetState}
                />
                <Button
                  variant="primary"
                  title="Apply Exclusions"
                  onClick={handleApplyChanges}
                  disabled={
                    updateTrainingConfigMutation.isLoading || !isModified
                  }
                />
              </div>
            </>
          ) : (
            <Button
              variant="primary"
              title="Edit"
              onClick={() => setIsEdit(true)}
            />
          )}
        </div>
      </div>
    </motion.div>
  )
}
